import { createTheme } from '@mui/material/styles';

// Crescer Design System — tokens --crescer-* (claude-skills/crescer-design-system).
// Fonte única de cor do frontend: use estes tokens em vez de hex literal no sx.
export const crescer = {
  // Superfícies e texto
  bg: '#F0F0F5',
  surface: '#ffffff',
  surface2: '#F7F7FA',
  border: '#D3D4DD',
  text: '#24252E',
  textSoft: '#515162',
  textFaint: '#696979',

  // Marca. Fill com texto pequeno usa brandDark (#007AF0 dá só 4,17:1 com branco;
  // #0069D0 dá 5,3:1 — AA). A marca pura fica em links, foco e superfícies grandes.
  brand: '#007AF0',
  brandLight: '#498FFF',
  brandDark: '#0069D0',
  brandDarker: '#0056AC',
  accent: '#02786D',
  secondary: '#515162',
  focusRing: '0 0 0 3px rgba(0,122,240,.15)',

  // Semânticas (cor + fundo)
  ok: '#217B00', okBg: '#E4F4E0',
  warn: '#E76F00', warnBg: '#FDEEDF',
  danger: '#D01E29', dangerBg: '#FBE2E4',
  info: '#007AF0', infoBg: '#E3F0FE',

  // Sidebar (slate escuro)
  sbGrad: 'linear-gradient(180deg, #0b1220 0%, #0f172a 42%, #16233b 100%)',
  sbBg: '#1e293b',
  sbHover: 'rgba(255,255,255,.06)',
  sbActive: '#1e40af',
  sbText: '#e2e8f0',
  sbMuted: '#94a3b8',
  sbAccent: '#60a5fa',
  sbBorder: 'rgba(255,255,255,.07)',

  // Forma e sombra
  radius: 8,
  radiusLg: 12,
  shadowCard: '0 1px 3px 0 rgb(0 0 0 / 0.08), 0 1px 2px -1px rgb(0 0 0 / 0.08)',
  shadowHover: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
  shadowModal: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
  font: '"IBM Plex Sans", "Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',

  // Gradiente de marca + séries de dados (usar na ordem)
  aiGrad: 'linear-gradient(90deg,#007AF0,#6D4AE0)',
  cat: ['#007AF0', '#0EA5A0', '#C98500', '#6D4AE0', '#E85AA0', '#00A46B', '#EB6834'],
} as const;

const shadowSm = crescer.shadowCard;
const shadowMd = crescer.shadowHover;
const shadowLg = '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)';
const shadowXl = crescer.shadowModal;

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      // passo escuro da marca: todo fill primário do MUI carrega texto branco pequeno
      main: crescer.brandDark,
      light: crescer.brand,
      dark: crescer.brandDarker,
      contrastText: '#ffffff',
    },
    secondary: {
      main: crescer.secondary,
      light: crescer.textFaint,
      dark: '#3A3A47',
      contrastText: '#ffffff',
    },
    success: {
      main: crescer.ok,
      light: '#54C241',
      dark: '#166000',
    },
    error: {
      main: crescer.danger,
      light: '#F75B60',
      dark: '#A4151E',
    },
    warning: {
      main: crescer.warn,
      light: '#FF8C54',
      dark: '#AD5000',
    },
    info: {
      main: crescer.info,
      light: crescer.brandLight,
      dark: crescer.brandDark,
    },
    background: {
      default: crescer.bg,
      paper: crescer.surface,
    },
    text: {
      primary: crescer.text,
      secondary: crescer.textSoft,
    },
    divider: crescer.border,
  },
  typography: {
    fontFamily: crescer.font,
    h1: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      fontSize: '1.75rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontWeight: 700,
      fontSize: '1.25rem',
      lineHeight: 1.4,
      letterSpacing: '-0.02em',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.4,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
      letterSpacing: '-0.01em',
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: '0.8125rem',
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.8125rem',
      lineHeight: 1.5,
    },
    button: {
      fontWeight: 500,
      fontSize: '0.8125rem',
      textTransform: 'none' as const,
      letterSpacing: '0.02em',
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.5,
    },
  },
  shape: {
    borderRadius: crescer.radius,
  },
  shadows: [
    'none',
    shadowSm, // shadow-card
    shadowMd, // shadow-hover
    shadowLg,
    shadowXl, // shadow-modal
    shadowSm,
    shadowMd,
    shadowSm,
    shadowMd,
    shadowLg,
    shadowXl,
    shadowSm,
    shadowMd,
    shadowLg,
    shadowXl,
    shadowSm,
    shadowMd,
    shadowLg,
    shadowXl,
    shadowSm,
    shadowMd,
    shadowLg,
    shadowXl,
    shadowSm,
    shadowMd,
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: crescer.bg,
          color: crescer.text,
          WebkitFontSmoothing: 'antialiased',
        },
        'a:focus-visible, button:focus-visible, [role="button"]:focus-visible, [tabindex]:not([tabindex="-1"]):focus-visible': {
          outline: `2px solid ${crescer.brand}`,
          outlineOffset: 2,
          borderRadius: 4,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: crescer.radius,
          padding: '10px 16px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: 'none',
          },
        },
        // botão "ghost" do kit: superfície própria, borda neutra, realce de marca no hover
        outlinedPrimary: {
          color: crescer.text,
          backgroundColor: crescer.surface,
          borderColor: crescer.border,
          '&:hover': {
            backgroundColor: crescer.surface2,
            borderColor: crescer.brand,
          },
        },
        sizeSmall: {
          padding: '6px 11px',
          fontSize: '0.75rem',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: crescer.radius,
          border: `1px solid ${crescer.border}`,
          boxShadow: crescer.shadowCard,
          transition: 'box-shadow 0.2s ease',
          '&:hover': {
            boxShadow: crescer.shadowHover,
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        title: {
          fontSize: '1rem',
          fontWeight: 600,
          letterSpacing: '-0.01em',
        },
        subheader: {
          fontSize: '0.78125rem',
          color: crescer.textFaint,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        outlined: {
          borderColor: crescer.border,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: crescer.radius,
          backgroundColor: crescer.surface,
          transition: 'box-shadow 0.15s ease',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: crescer.border,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: crescer.brand,
          },
          '&.Mui-focused': {
            boxShadow: crescer.focusRing,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: crescer.brand,
            borderWidth: 1,
          },
          '&.Mui-error.Mui-focused': {
            boxShadow: '0 0 0 3px rgba(208,30,41,.15)',
          },
          '&.Mui-disabled': {
            backgroundColor: crescer.surface2,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: crescer.textSoft,
          '&.Mui-focused': {
            color: crescer.brandDark,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '0.71875rem',
          color: crescer.textFaint,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: crescer.radius,
          fontWeight: 500,
        },
        sizeSmall: {
          fontSize: '0.71875rem',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: crescer.radius,
          fontSize: '0.8125rem',
        },
        standardSuccess: { backgroundColor: crescer.okBg, color: '#166000', '& .MuiAlert-icon': { color: crescer.ok } },
        standardWarning: { backgroundColor: crescer.warnBg, color: '#8A4000', '& .MuiAlert-icon': { color: crescer.warn } },
        standardError: { backgroundColor: crescer.dangerBg, color: '#A4151E', '& .MuiAlert-icon': { color: crescer.danger } },
        standardInfo: { backgroundColor: crescer.infoBg, color: crescer.brandDarker, '& .MuiAlert-icon': { color: crescer.info } },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: crescer.surface2,
            fontWeight: 600,
            fontSize: '0.6875rem',
            textTransform: 'uppercase' as const,
            letterSpacing: '0.04em',
            color: crescer.textFaint,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: crescer.border,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover': {
            backgroundColor: crescer.surface2,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          '&.Mui-selected': {
            fontWeight: 600,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: crescer.text,
          fontSize: '0.75rem',
          borderRadius: 6,
        },
        arrow: {
          color: crescer.text,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: crescer.radiusLg,
          boxShadow: crescer.shadowModal,
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          '&:not(.MuiBackdrop-invisible)': {
            backgroundColor: 'rgba(15, 23, 42, .45)',
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          fontWeight: 600,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: crescer.radius,
          border: `1px solid ${crescer.border}`,
          boxShadow: crescer.shadowHover,
        },
      },
    },
  },
});

export default theme;
